import { Box, IconButton, Tooltip } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { ReactElement } from 'react';
import styles from './styles.module.css';
import clsx from 'clsx';

type HeaderProps = {
    showTitle?: boolean;
    upperLabel?: string | any;
    lowerLabel?: string | any;
    titles?: any[];
    type: string;
    icon?: any;
    onClick?: any;
    template: (v: any, i?: any) => ReactElement;
    width?: string;
    maxWidth?: string;
    align?: 'right' | 'left' | 'center' | 'justify' | 'char' | undefined;
    noStyle?: boolean;
    maxLinesBeforeEllipsis?: number;
    showFullInfoTooltip?: boolean;
    centerColumn?: boolean;
    // Solo lo usan las grillas que necesitan achicar el icono de accion en mobile
    // (p. ej. Visitas), sin afectar al resto de las tablas que usan este componente.
    compactOnMobile?: boolean;
    /**
     * Agrupa filas: `(item) => n` hace que la celda abarque n filas; 0 la
     * omite porque ya la abarcó una fila anterior. Ver `DataTableRow`.
     */
    rowSpan?: (item: any) => number | undefined;
};

interface Props {
    header: HeaderProps;
    item: any;
    index: number;
    onSelectRow: any;
    menuOpen: boolean;
    selected: any;
    selectedItemFromAction: any;
    isMobile?: boolean;
}

function DataTableCell({
    header,
    item,
    index,
    menuOpen,
    selectedItemFromAction,
    isMobile
}: Props) {
    const isItemSelected = item?.id === selectedItemFromAction?.id;
    const hasMaxWidth = header?.maxWidth !== undefined && header?.maxWidth !== '';

    const DataCell = () => {
        const rawUpper =
            typeof header?.upperLabel === 'function'
                ? header.upperLabel(item)
                : item[header?.upperLabel];

        const rawLower =
            typeof header?.lowerLabel === 'function'
                ? header.lowerLabel(item)
                : item[header?.lowerLabel];
        
        const content = (
            <div
            className={clsx(
                hasMaxWidth ? styles['cell-content-with-max-width'] : '',
                header?.maxLinesBeforeEllipsis ? styles['cell-multiline-ellipsis'] : '',
                header?.centerColumn ? styles['cell-center'] : ''
            )}
            style={{
                ...(header?.maxLinesBeforeEllipsis
                ? { ['--line-clamp' as any]: header.maxLinesBeforeEllipsis }
                : {}),
                ...(header?.centerColumn ? { justifyContent: 'center' } : {})
            }}
            >
            <div className={styles.upperLabel}>{rawUpper}</div>
            {rawLower && <div className={styles.lowerLabel}>{rawLower}</div>}
            </div>
        );

        return (
            <div style={{ width: '100%' }}>
                {header?.showFullInfoTooltip ? (
                    <Tooltip title={`${rawUpper} ${rawLower ?? ''}`} placement="top-start">
                        {content}
                    </Tooltip>
                ) : (
                    content
                )}
            </div>
        );
    };

    /*  ${ &menuOpen& isItemSelected ? styles['iconbtn-active'] : ''} */

    const isCompactAction = Boolean(isMobile && header?.compactOnMobile);

    const ActionCell = () => (
        <IconButton
            className={clsx(styles['iconbtn-m'], isCompactAction && styles['iconbtn-compact'])}
            aria-label="menuOpciones"
            size={isCompactAction ? 'small' : 'medium'}
            sx={{
                border: (theme) =>
                    menuOpen && isItemSelected
                        ? `1.5px solid ${theme.palette.primary.main}`
                        : '1px solid #E0EBED',
                outline: (theme) =>
                    menuOpen && isItemSelected
                        ? `1.5px solid ${theme.palette.primary.main}`
                        : '1px solid #E0EBED',
            justifyContent: header?.centerColumn ? 'center' : undefined,
            alignItems: header?.centerColumn ? 'center' : undefined,

            }}
        >
            {header?.icon ? (
                header.icon
            ) : (
                <KeyboardArrowDownIcon fontSize={isCompactAction ? 'small' : 'medium'} />
            )}
        </IconButton>
    );

    const SelectionCell = () => (
        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            {header.template(item, index)}
        </Box>
    );

    function RenderTd() {
        switch (header?.type) {
            case 'selection':
                return <SelectionCell />;
            case 'action':
                return <ActionCell />;
            case 'element':
                return <SelectionCell />;
            case 'presentation-options':
                return <DataCell />;
            default:
                return <DataCell />;
        }
    }

    return <RenderTd />;
}

export default DataTableCell;
